<script setup>
import { ref, onMounted, computed } from 'vue'
import { useAuthStore } from '../../store/auth'
import { useContentStore } from '../../store/content'
import { useToast } from 'primevue/usetoast'
import { useRouter } from 'vue-router'

const authStore = useAuthStore()
const contentStore = useContentStore()
const toast = useToast()
const router = useRouter()

// Check if user is admin
const isAdmin = computed(() => authStore.isAdmin)

// Section management
const selectedSection = ref(null)
const sectionDocuments = ref([])
const uploadedFiles = ref([])
const loading = ref(false)
const showUploadDialog = ref(false)

const categoryLabels = {
  'informacion-entidad': 'Información de la entidad',
  normativa: 'Normativa',
  contratacion: 'Contratación',
  planeacion: 'Planeación y presupuesto',
  tramites: 'Trámites',
  participa: 'Participa',
  'datos-abiertos': 'Datos abiertos',
  'grupos-interes': 'Grupos de interés',
  'informacion-especifica': 'Información específica',
  'informacion-tributaria': 'Información tributaria'
}

const categoryIcons = {
  'informacion-entidad': 'pi pi-building',
  normativa: 'pi pi-book',
  contratacion: 'pi pi-briefcase',
  planeacion: 'pi pi-chart-line',
  tramites: 'pi pi-check-square',
  participa: 'pi pi-users',
  'datos-abiertos': 'pi pi-database',
  'grupos-interes': 'pi pi-heart',
  'informacion-especifica': 'pi pi-file',
  'informacion-tributaria': 'pi pi-calculator'
}

// List of all available sections
const sections = ref([
  // 1. Información de la entidad
  { id: 'mision-vision', name: 'Misión, visión, funciones y deberes', parent: 'informacion-entidad' },
  { id: 'estructura-organica', name: 'Estructura orgánica - Organigrama', parent: 'informacion-entidad' },
  { id: 'mapas-procesos', name: 'Mapas y Cartas descriptivas de los procesos', parent: 'informacion-entidad' },
  { id: 'directorio-institucional', name: 'Directorio Institucional', parent: 'informacion-entidad' },
  { id: 'directorio-servidores', name: 'Directorio de servidores públicos', parent: 'informacion-entidad' },
  { id: 'directorio-entidades', name: 'Directorio de entidades', parent: 'informacion-entidad' },
  { id: 'directorio-agremiaciones', name: 'Directorio de agremiaciones', parent: 'informacion-entidad' },
  { id: 'procedimientos-decisiones', name: 'Procedimientos para tomar decisiones', parent: 'informacion-entidad' },
  { id: 'mecanismo-solicitudes', name: 'Mecanismo de presentación de solicitudes', parent: 'informacion-entidad' },
  { id: 'calendario-actividades', name: 'Calendario de actividades y eventos', parent: 'informacion-entidad' },
  { id: 'decisiones-publico', name: 'Información sobre decisiones', parent: 'informacion-entidad' },
  { id: 'entes-vigilancia', name: 'Entes y autoridades que lo vigilan', parent: 'informacion-entidad' },
  { id: 'hojas-vida', name: 'Publicación de hojas de vida', parent: 'informacion-entidad' },
  
  // 2. Normativa
  { id: 'leyes', name: 'Leyes', parent: 'normativa' },
  { id: 'decreto-unico', name: 'Decreto Único Reglamentario', parent: 'normativa' },
  { id: 'normativa-aplicable', name: 'Normativa aplicable', parent: 'normativa' },
  { id: 'diario-oficial', name: 'Vínculo al Diario o Gaceta Oficial', parent: 'normativa' },
  { id: 'politicas-lineamientos', name: 'Políticas, lineamientos y manuales', parent: 'normativa' },
  { id: 'agenda-regulatoria', name: 'Agenda Regulatoria', parent: 'normativa' },
  { id: 'suin', name: 'Sistema Único de Información Normativa', parent: 'normativa' },
  { id: 'sistema-busqueda', name: 'Sistema de búsquedas de normas', parent: 'normativa' },
  { id: 'proyectos-normativos', name: 'Proyectos normativos', parent: 'normativa' },
  { id: 'comentarios-respuesta', name: 'Comentarios y documento de respuesta', parent: 'normativa' },
  { id: 'participacion-sucop', name: 'Participación ciudadana SUCOP', parent: 'normativa' },
  
  // 3. Contratación
  { id: 'plan-adquisiciones', name: 'Plan Anual de Adquisiciones', parent: 'contratacion' },
  { id: 'info-contractual', name: 'Publicación de la información contractual', parent: 'contratacion' },
  { id: 'ejecucion-contratos', name: 'Publicación de la ejecución de los contratos', parent: 'contratacion' },
  { id: 'manual-contratacion', name: 'Manual de contratación', parent: 'contratacion' },
  { id: 'formatos-contratos', name: 'Formatos o modelos de contratos', parent: 'contratacion' },
  
  // 4. Planeación, Presupuesto e Informes
  { id: 'presupuesto-general', name: 'Presupuesto general', parent: 'planeacion' },
  { id: 'ejecucion-presupuestal', name: 'Ejecución presupuestal', parent: 'planeacion' },
  { id: 'plan-accion', name: 'Plan de Acción', parent: 'planeacion' },
  { id: 'proyectos-inversion', name: 'Proyectos de Inversión', parent: 'planeacion' },
  { id: 'informes-empalme', name: 'Informes de empalme', parent: 'planeacion' },
  { id: 'info-publica', name: 'Información pública y/o relevante', parent: 'planeacion' },
  { id: 'informes-gestion', name: 'Informes de gestión', parent: 'planeacion' },
  { id: 'informes-control-interno', name: 'Informes de la Oficina de Control Interno', parent: 'planeacion' },
  { id: 'informe-defensa', name: 'Informe sobre Defensa Pública', parent: 'planeacion' },
  { id: 'informes-acceso', name: 'Informes trimestrales sobre acceso a información', parent: 'planeacion' },
  
  // 5. Trámites
  { id: 'tramites-servicios', name: 'Trámites y servicios', parent: 'tramites' },
  
  // 6. Participa
  { id: 'participa-descripcion', name: 'Participa (Descripción general)', parent: 'participa' },
  { id: 'diagnostico-necesidades', name: 'Participación para el diagnóstico de necesidades', parent: 'participa' },
  { id: 'planeacion-participativa', name: 'Planeación y presupuesto participativo', parent: 'participa' },
  { id: 'consulta-ciudadana', name: 'Consulta ciudadana', parent: 'participa' },
  { id: 'colaboracion-innovacion', name: 'Colaboración e innovación abierta', parent: 'participa' },
  { id: 'rendicion-cuentas', name: 'Rendición de cuentas', parent: 'participa' },
  { id: 'control-social', name: 'Control social', parent: 'participa' },
  
  // 7. Datos abiertos
  { id: 'gestion-informacion', name: 'Instrumentos de gestión de la información', parent: 'datos-abiertos' },
  { id: 'datos-abiertos', name: 'Sección de Datos Abiertos', parent: 'datos-abiertos' },
  
  // 8. Información específica para Grupos de Interés
  { id: 'info-ninos', name: 'Información para niños, niñas y adolescentes', parent: 'grupos-interes' },
  { id: 'info-mujeres', name: 'Información para Mujeres', parent: 'grupos-interes' },
  
  // 9. Obligación de reporte
  { id: 'info-especifica', name: 'Información, documentos, reportes específicos', parent: 'informacion-especifica' },
  
  // 10. Información tributaria
  { id: 'recaudo-rentas', name: 'Procesos de recaudo de rentas locales', parent: 'informacion-tributaria' },
  { id: 'tarifas-ica', name: 'Tarifas de liquidación del Impuesto ICA', parent: 'informacion-tributaria' }
])

// Group sections by parent
const groupedSections = computed(() => {
  const groups = {}
  
  sections.value.forEach(section => {
    if (!groups[section.parent]) {
      groups[section.parent] = []
    }
    groups[section.parent].push(section)
  })
  
  return groups
})

const parentEntries = computed(() => Object.entries(groupedSections.value))
const totalSections = computed(() => sections.value.length)
const activeParentLabel = computed(() => selectedSection.value ? getParentLabel(selectedSection.value.parent) : 'Sin sección')

const getParentLabel = (parent) => categoryLabels[parent] || parent
const getParentIcon = (parent) => categoryIcons[parent] || 'pi pi-folder'

const formatSize = (size) => {
  if (!size) return 'No disponible'
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const formatDate = (date) => {
  if (!date) return 'Sin fecha'
  return new Date(date).toLocaleDateString('es-CO')
}

// Load section content
const loadSectionContent = async (section) => {
  if (!section) return
  
  selectedSection.value = section
  loading.value = true
  
  try {
    // Load section documents
    const docs = await contentStore.getSectionDocuments(section.id)
    sectionDocuments.value = docs || []
  } catch (error) {
    console.error('Error cargando contenido:', error)
    toast.add({ severity: 'error', summary: 'Error', detail: 'Error al cargar el contenido', life: 3000 })
  } finally {
    loading.value = false
  }
}

// Upload document
const openUploadDialog = () => {
  showUploadDialog.value = true
  uploadedFiles.value = []
}

const handleFileInputChange = (event) => {
  uploadedFiles.value = Array.from(event.target.files || [])
}

const uploadDocument = async () => {
  if (!selectedSection.value || uploadedFiles.value.length === 0) return
  
  loading.value = true
  
  try {
    for (const file of uploadedFiles.value) {
      await contentStore.uploadDocument(selectedSection.value.id, file)
    }
    
    // Reload documents
    await loadSectionContent(selectedSection.value)
    showUploadDialog.value = false
    toast.add({ severity: 'success', summary: 'Éxito', detail: 'Documento(s) subido(s) correctamente', life: 3000 })
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error al subir', detail: error.message || 'Error al subir el documento', life: 6000 })
  } finally {
    loading.value = false
  }
}

// Download document
const downloadDocument = (url) => {
  window.open(url, '_blank')
}

// Delete document
const deleteDocument = async (documentId) => {
  if (!documentId) return
  
  loading.value = true
  
  try {
    await contentStore.deleteDocument(documentId)
    
    // Reload documents
    await loadSectionContent(selectedSection.value)
    toast.add({ severity: 'success', summary: 'Éxito', detail: 'Documento eliminado correctamente', life: 3000 })
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Error al eliminar el documento', life: 3000 })
  } finally {
    loading.value = false
  }
}

const goToSetup = () => {
  router.push('/portal-transparencia/setup')
}

const goToPortal = () => {
  router.push('/portal-transparencia')
}

const logout = async () => {
  await authStore.logout()
  router.push('/portal-transparencia/login')
}

// Check if user is admin on mount
onMounted(async () => {
  if (!isAdmin.value) {
    toast.add({ severity: 'error', summary: 'Acceso denegado', detail: 'No tiene permisos para acceder a esta página', life: 3000 })
    return
  }
  
  // Initialize document types
  await contentStore.initializeDocumentTypes()

  if (!selectedSection.value && sections.value.length > 0) {
    await loadSectionContent(sections.value[0])
  }
})
</script>

<template>
  <main class="admin-page" v-if="isAdmin">
    <Toast />

    <header class="admin-topbar">
      <div class="topbar-copy">
        <router-link to="/portal-transparencia" class="back-link">
          <i class="pi pi-arrow-left" aria-hidden="true"></i>
          Portal público
        </router-link>
        <p class="admin-eyebrow">Portal interno</p>
        <h1>Gestión de documentos</h1>
        <p>Seleccione una sección, revise sus archivos publicados y cargue nuevas versiones del portal de transparencia.</p>
      </div>

      <div class="topbar-actions">
        <Button label="Configuración" icon="pi pi-cog" severity="secondary" outlined @click="goToSetup" />
        <Button label="Ver portal" icon="pi pi-external-link" severity="secondary" outlined @click="goToPortal" />
        <Button label="Salir" icon="pi pi-sign-out" severity="danger" text @click="logout" />
      </div>
    </header>

    <section class="admin-summary" aria-label="Resumen de gestión">
      <div class="summary-item">
        <span>Total secciones</span>
        <strong>{{ totalSections }}</strong>
      </div>
      <div class="summary-item">
        <span>Categoría activa</span>
        <strong>{{ activeParentLabel }}</strong>
      </div>
      <div class="summary-item">
        <span>Documentos en sección</span>
        <strong>{{ sectionDocuments.length }}</strong>
      </div>
    </section>

    <div class="admin-workspace">
      <aside class="section-sidebar" aria-label="Secciones del portal">
        <div class="sidebar-header">
          <div>
            <span>Mapa del portal</span>
            <strong>{{ parentEntries.length }} categorías</strong>
          </div>
        </div>

        <div class="category-list">
          <section v-for="([parent, parentSections]) in parentEntries" :key="parent" class="category-block">
            <h2>
              <i :class="getParentIcon(parent)" aria-hidden="true"></i>
              <span>{{ getParentLabel(parent) }}</span>
              <small>{{ parentSections.length }}</small>
            </h2>

            <button
              v-for="section in parentSections"
              :key="section.id"
              type="button"
              class="section-item"
              :class="{ selected: selectedSection?.id === section.id }"
              @click="loadSectionContent(section)"
            >
              <span>{{ section.name }}</span>
            </button>
          </section>
        </div>
      </aside>

      <section class="document-panel">
        <div v-if="selectedSection" class="panel-card">
          <div class="panel-header">
            <div>
              <p>{{ activeParentLabel }}</p>
              <h2>{{ selectedSection.name }}</h2>
            </div>
            <button type="button" class="upload-action-button panel-upload-button" @click="openUploadDialog">
              <i class="pi pi-upload" aria-hidden="true"></i>
              Cargar documento
            </button>
          </div>

          <DataTable
            :value="sectionDocuments"
            :loading="loading"
            paginator
            :rows="8"
            responsiveLayout="scroll"
            class="documents-table"
            emptyMessage="No hay documentos cargados para esta sección"
          >
            <Column field="name" header="Documento" sortable>
              <template #body="slotProps">
                <div class="document-name">
                  <span class="document-icon">
                    <i class="pi pi-file" aria-hidden="true"></i>
                  </span>
                  <div>
                    <strong>{{ slotProps.data.name }}</strong>
                    <span v-if="slotProps.data.comentario">{{ slotProps.data.comentario }}</span>
                    <span v-else>Archivo publicado en el portal</span>
                  </div>
                </div>
              </template>
            </Column>
            <Column field="type" header="Tipo">
              <template #body="slotProps">
                {{ slotProps.data.type || 'Archivo' }}
              </template>
            </Column>
            <Column field="size" header="Tamaño">
              <template #body="slotProps">
                {{ formatSize(slotProps.data.size) }}
              </template>
            </Column>
            <Column field="uploadedAt" header="Publicado">
              <template #body="slotProps">
                {{ formatDate(slotProps.data.uploadedAt) }}
              </template>
            </Column>
            <Column header="Acciones" bodyStyle="text-align: right; white-space: nowrap;">
              <template #body="slotProps">
                <Button
                  icon="pi pi-download"
                  class="p-button-text p-button-sm"
                  aria-label="Descargar documento"
                  @click="downloadDocument(slotProps.data.url)"
                />
                <Button
                  icon="pi pi-trash"
                  class="p-button-text p-button-danger p-button-sm"
                  aria-label="Eliminar documento"
                  @click="deleteDocument(slotProps.data.id)"
                />
              </template>
            </Column>
          </DataTable>
        </div>

        <div v-else class="empty-state">
          <span class="empty-icon">
            <i class="pi pi-folder-open" aria-hidden="true"></i>
          </span>
          <h2>Seleccione una sección</h2>
          <p>Elija una sección del mapa del portal para cargar y administrar sus documentos.</p>
        </div>
      </section>
    </div>

    <Dialog v-model:visible="showUploadDialog" header="Cargar documento" :style="{ width: '540px', maxWidth: '92vw' }" modal>
      <p class="upload-dialog-help">
        Seleccione el archivo que desea publicar en la sección activa y luego confirme la carga.
      </p>

      <label class="native-upload-picker" for="admin-document-upload">
        <i class="pi pi-cloud-upload" aria-hidden="true"></i>
        <span>Seleccionar archivo</span>
        <small>PDF, Word, Excel o imagen hasta 50 MB</small>
      </label>
      <input
        id="admin-document-upload"
        class="native-file-input"
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
        @change="handleFileInputChange"
      />
      
      <div v-if="uploadedFiles.length > 0" class="selected-files">
        <h4>Archivos seleccionados</h4>
        <ul>
          <li v-for="(file, index) in uploadedFiles" :key="index">{{ file.name }}</li>
        </ul>
      </div>

      <div class="modal-actions">
        <button type="button" class="modal-cancel-button" @click="showUploadDialog = false">
          <i class="pi pi-times" aria-hidden="true"></i>
          Cancelar
        </button>
        <button
          type="button"
          class="modal-upload-button"
          :disabled="uploadedFiles.length === 0 || loading"
          @click="uploadDocument"
        >
          <i class="pi pi-upload" aria-hidden="true"></i>
          {{ loading ? 'Subiendo...' : 'Subir documento' }}
        </button>
      </div>
      
    </Dialog>
  </main>
  
  <div v-else class="access-denied">
    <i class="pi pi-lock" aria-hidden="true"></i>
    <h2>Acceso denegado</h2>
    <p>No tiene permisos para acceder a esta página.</p>
  </div>
</template>

<style scoped>
.admin-page {
  min-height: 100vh;
  padding: 28px 24px 56px;
  background: #f5f8f5;
  color: #102716;
}

.admin-topbar,
.admin-summary,
.admin-workspace {
  max-width: 1280px;
  margin: 0 auto;
}

.admin-topbar {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.back-link {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 18px;
  color: #19642d;
  font-weight: 800;
  text-decoration: none;
}

.admin-eyebrow {
  margin: 0 0 8px;
  color: #008300;
  font-size: 0.8rem;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.topbar-copy h1 {
  margin: 0;
  color: #102716;
  font-size: clamp(2rem, 4vw, 3.2rem);
  line-height: 1.05;
  letter-spacing: 0;
}

.topbar-copy p:last-child {
  max-width: 720px;
  margin: 12px 0 0;
  color: #627363;
  line-height: 1.55;
}

.topbar-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.upload-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #008300;
  box-shadow: 0 12px 26px rgba(0, 131, 0, 0.24);
  cursor: pointer;
  font-weight: 900;
  letter-spacing: 0;
}

.upload-action-button:hover:not(:disabled) {
  background: #006f00;
}

.upload-action-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.admin-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.summary-item {
  min-height: 86px;
  padding: 16px;
  border: 1px solid #e1ebe3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 28px rgba(15, 55, 24, 0.06);
}

.summary-item span {
  display: block;
  margin-bottom: 8px;
  color: #647665;
  font-size: 0.82rem;
  font-weight: 800;
  text-transform: uppercase;
}

.summary-item strong {
  color: #102716;
  font-size: 1.35rem;
  line-height: 1.15;
}

.admin-workspace {
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.section-sidebar,
.panel-card,
.empty-state {
  border: 1px solid #e1ebe3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 14px 34px rgba(15, 55, 24, 0.08);
}

.section-sidebar {
  position: sticky;
  top: 18px;
  overflow: hidden;
}

.sidebar-header {
  padding: 18px;
  border-bottom: 1px solid #e7efe9;
}

.sidebar-header span {
  display: block;
  color: #102716;
  font-weight: 900;
}

.sidebar-header strong {
  display: block;
  margin-top: 4px;
  color: #647665;
  font-size: 0.88rem;
}

.category-list {
  max-height: calc(100vh - 220px);
  overflow-y: auto;
  padding: 12px;
}

.category-block + .category-block {
  margin-top: 14px;
}

.category-block h2 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  padding: 0 4px;
  color: #19642d;
  font-size: 0.9rem;
  letter-spacing: 0;
}

.category-block h2 small {
  margin-left: auto;
  color: #708473;
  font-weight: 900;
}

.section-item {
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 8px;
  color: #304b39;
  background: transparent;
  font-weight: 700;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.section-item:hover,
.section-item:focus-visible {
  color: #0f3f1f;
  background: #f0f7f1;
  outline: none;
}

.section-item.selected {
  color: #ffffff;
  background: #008300;
  box-shadow: 0 8px 18px rgba(0, 131, 0, 0.16);
}

.document-panel {
  min-width: 0;
}

.panel-card {
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-start;
  padding: 22px;
  border-bottom: 1px solid #e7efe9;
  background: linear-gradient(135deg, rgba(0, 131, 0, 0.08), rgba(29, 78, 216, 0.05));
}

.panel-header p {
  margin: 0 0 8px;
  color: #008300;
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
}

.panel-header h2 {
  margin: 0;
  color: #102716;
  font-size: clamp(1.35rem, 3vw, 2rem);
  line-height: 1.15;
  letter-spacing: 0;
}

.panel-upload-button {
  flex: 0 0 auto;
}

.documents-table {
  border-radius: 0;
}

.document-name {
  display: flex;
  gap: 12px;
  align-items: center;
  min-width: 260px;
}

.document-icon,
.empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.document-icon {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  color: #008300;
  background: #e8f6ea;
}

.document-name strong,
.document-name span {
  display: block;
}

.document-name strong {
  max-width: 420px;
  overflow: hidden;
  color: #102716;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-name span {
  max-width: 420px;
  overflow: hidden;
  margin-top: 3px;
  color: #647665;
  font-size: 0.86rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-state {
  min-height: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  color: #647665;
  text-align: center;
}

.empty-icon {
  width: 58px;
  height: 58px;
  margin-bottom: 16px;
  color: #008300;
  background: #e8f6ea;
  font-size: 1.4rem;
}

.empty-state h2 {
  margin: 0 0 8px;
  color: #102716;
  font-size: 1.35rem;
}

.empty-state p {
  max-width: 430px;
  margin: 0;
  line-height: 1.5;
}

.upload-dialog-help {
  margin: 0 0 16px;
  color: #536756;
  line-height: 1.5;
}

.native-upload-picker {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  margin-bottom: 18px;
  padding: 24px;
  border: 2px dashed #9ccfa8;
  border-radius: 8px;
  color: #19642d;
  background: #f7fcf8;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.native-upload-picker:hover {
  border-color: #008300;
  background: #eef8f0;
}

.native-upload-picker i {
  margin-bottom: 10px;
  font-size: 2rem;
}

.native-upload-picker span {
  font-weight: 900;
}

.native-upload-picker small {
  margin-top: 6px;
  color: #647665;
  font-weight: 700;
}

.native-file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

.selected-files {
  padding: 14px;
  border: 1px solid #d8e8dc;
  border-radius: 8px;
  background: #f8fcf9;
}

.selected-files h4 {
  margin: 0 0 10px;
  color: #102716;
  font-size: 0.95rem;
}

.selected-files ul {
  margin: 0;
  padding-left: 18px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e1ebe3;
}

.modal-cancel-button,
.modal-upload-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 900;
}

.modal-cancel-button {
  border: 1px solid #cbd8ce;
  color: #304b39;
  background: #ffffff;
}

.modal-upload-button {
  border: 0;
  color: #ffffff;
  background: #008300;
  box-shadow: 0 12px 26px rgba(0, 131, 0, 0.22);
}

.modal-upload-button:hover:not(:disabled) {
  background: #006f00;
}

.modal-upload-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.access-denied {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: #647665;
  background: #f5f8f5;
  text-align: center;
}

.access-denied i {
  margin-bottom: 16px;
  color: #008300;
  font-size: 3rem;
}

.access-denied h2 {
  margin: 0 0 8px;
  color: #102716;
}

@media (max-width: 1080px) {
  .admin-topbar {
    flex-direction: column;
  }

  .topbar-actions {
    justify-content: flex-start;
  }

  .admin-workspace {
    grid-template-columns: 1fr;
  }

  .section-sidebar {
    position: static;
  }

  .category-list {
    max-height: none;
  }
}

@media (max-width: 720px) {
  .admin-page {
    padding: 22px 14px 42px;
  }

  .admin-summary {
    grid-template-columns: 1fr;
  }

  .panel-header {
    flex-direction: column;
  }

  .panel-header :deep(.p-button) {
    width: 100%;
  }

  .panel-upload-button,
  .upload-action-button {
    width: 100%;
  }

  .modal-actions {
    flex-direction: column-reverse;
  }

  .modal-cancel-button,
  .modal-upload-button {
    width: 100%;
  }
}
</style>
